/**
 * Agents Management Page
 *
 * CRUD for managing financial agents (persons, assets, pets)
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Search,
  User,
  Car,
  PawPrint,
  Edit,
  Trash2,
  MoreHorizontal,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

import {
  useAgents,
  useCreateAgent,
  useUpdateAgent,
  useDeleteAgent,
} from '@/hooks/use-financial';
import { toast } from '@/hooks/use-toast';
import type { Agent, AgentUpdate, AgentType } from '@/types/financial';
import {
  FIRST_ACCESS_TUTORIAL_STEPS,
  findTutorialCreatedItem,
  markTutorialKnownItemIds,
  requestTutorialAdvance,
  useActiveOnboardingStep,
} from '@/components/onboarding/tutorial-runtime';

const TUTORIAL_SCOPE = 'agents';

// Realce do botão de criação quando o formulário está completo.
const CREATE_READY_BUTTON_CLASS =
  'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-600';

const AGENT_TYPE_CONFIG: Record<AgentType, { label: string; icon: React.ElementType; color: string }> = {
  person: { label: 'Pessoa', icon: User, color: 'bg-blue-100 text-blue-700' },
  asset: { label: 'Bem', icon: Car, color: 'bg-amber-100 text-amber-700' },
  pet: { label: 'Animal', icon: PawPrint, color: 'bg-green-100 text-green-700' },
};

const LEGACY_DEFAULT_AGENT_DESCRIPTION = 'Agente padrão definido no primeiro acesso';

export function AgentsPage() {
  const activeTutorialStep = useActiveOnboardingStep();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  // Form states
  const [formData, setFormData] = useState<{
    name: string;
    agent_type?: AgentType;
    description: string;
    is_default_agent: boolean;
    is_default_payer: boolean;
    is_default_receiver: boolean;
  }>({
    name: '',
    agent_type: undefined,
    description: '',
    is_default_agent: false,
    is_default_payer: false,
    is_default_receiver: false,
  });
  
  // Hooks
  const { data: agents, isLoading } = useAgents(showInactive);
  const { mutate: createAgent, isPending: isCreating } = useCreateAgent();
  const { mutate: updateAgent, isPending: isUpdating } = useUpdateAgent();
  const { mutate: deleteAgent, isPending: isDeleting } = useDeleteAgent();
  
  // Filter agents
  const filteredAgents = agents?.filter((agent) => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || agent.agent_type === typeFilter;
    return matchesSearch && matchesType;
  }) || [];
  
  // Group agents by type
  const personAgents = filteredAgents.filter(a => a.agent_type === 'person');
  const assetAgents = filteredAgents.filter(a => a.agent_type === 'asset');
  const petAgents = filteredAgents.filter(a => a.agent_type === 'pet');
  const tutorialAgent = useMemo(
    () => findTutorialCreatedItem(TUTORIAL_SCOPE, agents),
    [agents]
  );

  /** Nome e tipo são o mínimo para criar; o resto é opcional. */
  const isCreateFormComplete = Boolean(formData.name && formData.agent_type);

  useEffect(() => {
    if (activeTutorialStep !== FIRST_ACCESS_TUTORIAL_STEPS.agentSave) return;
    // Fotografa o que já existe: o agente que sobrar depois é o que o usuário
    // acabou de cadastrar no tutorial.
    markTutorialKnownItemIds(TUTORIAL_SCOPE, (agents || []).map((agent) => agent.id));
    setIsCreateDialogOpen(true);
  }, [activeTutorialStep, agents]);

  useEffect(() => {
    if (activeTutorialStep !== FIRST_ACCESS_TUTORIAL_STEPS.agentSave || !isCreateDialogOpen) return;

    setFormData({
      // Vazio de propósito: o usuário cadastra um agente real dele.
      name: tutorialAgent?.name || '',
      agent_type: tutorialAgent?.agent_type || 'person',
      description: tutorialAgent?.description || '',
      is_default_agent: false,
      is_default_payer: false,
      is_default_receiver: false,
    });
  }, [activeTutorialStep, isCreateDialogOpen, tutorialAgent]);
  
  // Handlers
  const handleCreate = () => {
    if (!formData.name || !formData.agent_type) {
      toast({
        title: 'Preencha nome e tipo do agente',
        variant: 'destructive',
      });
      return;
    }

    createAgent({
      name: formData.name,
      agent_type: formData.agent_type,
      description: formData.description || undefined,
      is_default_payer: formData.is_default_payer,
      is_default_receiver: formData.is_default_receiver,
      is_default_agent: formData.is_default_agent,
    }, {
      onSuccess: () => {
        if (activeTutorialStep === FIRST_ACCESS_TUTORIAL_STEPS.agentSave) {
          requestTutorialAdvance(FIRST_ACCESS_TUTORIAL_STEPS.agentSave);
        }
        setIsCreateDialogOpen(false);
        setFormData({
          name: '',
          agent_type: undefined,
          description: '',
          is_default_agent: false,
          is_default_payer: false,
          is_default_receiver: false,
        });
      },
    });
  };
  
  const handleEdit = () => {
    if (!selectedAgent) return;

    const updateData: AgentUpdate = {
      name: formData.name,
      description: formData.description || undefined,
      is_default_payer: formData.is_default_payer,
      is_default_receiver: formData.is_default_receiver,
      is_default_agent: formData.is_default_agent,
    };

    updateAgent(
      { id: selectedAgent.id, data: updateData },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setSelectedAgent(null);
        },
      }
    );
  };
  
  const handleDelete = () => {
    if (!selectedAgent) return;
    
    deleteAgent(selectedAgent.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setSelectedAgent(null);
      },
    });
  };
  
  const handleToggleActive = (agent: Agent) => {
    updateAgent({
      id: agent.id,
      data: { is_active: !agent.is_active },
    });
  };
  
  const openEditDialog = (agent: Agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name,
      agent_type: agent.agent_type,
      description: agent.description || '',
      is_default_agent: agent.is_default_agent,
      is_default_payer: agent.is_default_payer,
      is_default_receiver: agent.is_default_receiver,
    });
    setIsEditDialogOpen(true);
  };
  
  const openDeleteDialog = (agent: Agent) => {
    setSelectedAgent(agent);
    setIsDeleteDialogOpen(true);
  };
  
  const renderAgentCard = (agent: Agent) => {
    const config = AGENT_TYPE_CONFIG[agent.agent_type];
    const Icon = config.icon;
    const isFirstAccessDefaultAgentDescription =
      (agent.description || '').trim() === LEGACY_DEFAULT_AGENT_DESCRIPTION;
    const visibleDescription =
      isFirstAccessDefaultAgentDescription && !agent.is_default_agent
        ? ''
        : (agent.description || '');
    
    return (
      <Card
        key={agent.id}
        className={`relative ${!agent.is_active ? 'opacity-50' : ''}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium">{agent.name}</h3>
                  {agent.is_default_agent && (
                    <Badge variant="default" className="text-xs bg-primary">
                      Agente Padrão
                    </Badge>
                  )}
                  {agent.is_default_receiver && (
                    <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-600">
                      Tem receita
                    </Badge>
                  )}
                  {agent.is_default_payer && (
                    <Badge variant="outline" className="text-xs border-rose-500 text-rose-600">
                      Paga contas
                    </Badge>
                  )}
                  {!agent.is_active && (
                    <Badge variant="secondary" className="text-xs">
                      Inativo
                    </Badge>
                  )}
                </div>
                {visibleDescription && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {visibleDescription}
                  </p>
                )}
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditDialog(agent)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleToggleActive(agent)}>
                  {agent.is_active ? (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      Desativar
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Ativar
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  disabled={agent.is_default_agent}
                  onClick={() => openDeleteDialog(agent)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {agent.is_default_agent ? 'Agente Padrão — não pode excluir' : 'Excluir'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="flex-1 space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gestão de Agentes</h1>
          <p className="text-muted-foreground">
            Gerencie pessoas, bens e animais associados às suas finanças
          </p>
        </div>
        
        <Button onClick={() => setIsCreateDialogOpen(true)} data-tour-id="create-agent">
          <Plus className="h-4 w-4 mr-2" />
          Novo Agente
        </Button>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar agentes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-9"
              />
              {searchTerm.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchTerm('')}
                  aria-label="Limpar busca de agentes"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="person">Pessoas</SelectItem>
                <SelectItem value="asset">Bens</SelectItem>
                <SelectItem value="pet">Animais</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="showInactive"
                checked={showInactive}
                onCheckedChange={(checked) => setShowInactive(!!checked)}
              />
              <Label htmlFor="showInactive" className="text-sm cursor-pointer">
                Mostrar inativos
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Content */}
      {(() => {
        if (isLoading) return (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (filteredAgents.length === 0) return (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <User className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum agente encontrado</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Você ainda não cadastrou nenhum agente. Crie seu primeiro agente para
                  associá-lo às suas receitas e despesas.
                </p>
                <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeiro Agente
                </Button>
              </CardContent>
            </Card>
        );
        return (
            <div className="space-y-6">
              {/* Pessoas */}
              {(typeFilter === 'all' || typeFilter === 'person') && personAgents.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-600" />
                    Pessoas ({personAgents.length})
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {personAgents.map(renderAgentCard)}
                  </div>
                </div>
              )}
          
              {/* Bens */}
              {(typeFilter === 'all' || typeFilter === 'asset') && assetAgents.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Car className="h-5 w-5 text-amber-600" />
                    Bens ({assetAgents.length})
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {assetAgents.map(renderAgentCard)}
                  </div>
                </div>
              )}
          
              {/* Pets */}
              {(typeFilter === 'all' || typeFilter === 'pet') && petAgents.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <PawPrint className="h-5 w-5 text-green-600" />
                    Animais ({petAgents.length})
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {petAgents.map(renderAgentCard)}
                  </div>
                </div>
              )}
            </div>
        );
      })()}
      
      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-tour-id="agent-create-dialog" className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-center">NOVO AGENTE</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              <span className="block">
                São pessoas, bens e animais de estimação que, em algum momento, irão gerar Receitas e/ou Despesas em seu
                orçamento. Exemplo: Seu cônjuge, filho, casa, carro e totô.
              </span>
              <span className="block mt-2">Utilize o primeiro nome ou apelido.</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="agent_name">1 - Nome</Label>
              <Input
                id="agent_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome do agente"
              />
            </div>

            <div className="space-y-2">
              <Label>2 - Tipo</Label>
              <Select
                value={formData.agent_type}
                onValueChange={(value) => setFormData({ ...formData, agent_type: value as AgentType })}
              >
                <SelectTrigger aria-label="Tipo">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">Pessoa</SelectItem>
                  <SelectItem value="asset">Bem</SelectItem>
                  <SelectItem value="pet">Animal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-foreground">Tem receita?</span>
                <Checkbox
                  aria-label="Tem receita?"
                  checked={formData.is_default_receiver}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      is_default_receiver: !!checked,
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-foreground">Paga contas?</span>
                <Checkbox
                  aria-label="Paga contas?"
                  checked={formData.is_default_payer}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      is_default_payer: !!checked,
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-foreground">Agente Padrão?</span>
                <Checkbox
                  aria-label="Agente Padrão?"
                  checked={formData.is_default_agent}
                  onCheckedChange={(checked) => {
                    setFormData({
                      ...formData,
                      is_default_agent: !!checked,
                    });
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent_description">Descrição:</Label>
              <Textarea
                id="agent_description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Opcional"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              // Formulário completo fica verde: sinaliza que já pode criar.
              className={isCreateFormComplete && !isCreating ? CREATE_READY_BUTTON_CLASS : undefined}
              disabled={!isCreateFormComplete || isCreating}
              data-tour-id="submit-agent"
            >
              {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Criar Agente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar Agente</DialogTitle>
            <DialogDescription>
              Atualize as informações do agente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit_name">Nome *</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Tipo</Label>
              <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                {selectedAgent && (
                  <>
                    {(() => {
                      const config = AGENT_TYPE_CONFIG[selectedAgent.agent_type];
                      const Icon = config.icon;
                      return (
                        <>
                          <Icon className="h-4 w-4" />
                          <span className="text-sm">{config.label}</span>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                O tipo não pode ser alterado após a criação.
              </p>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit_description">Descrição</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium">Configurações do agente:</p>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit_is_default_receiver"
                  checked={formData.is_default_receiver}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_default_receiver: !!checked })
                  }
                />
                <Label htmlFor="edit_is_default_receiver" className="text-sm cursor-pointer">
                  Tem receita
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit_is_default_payer"
                  checked={formData.is_default_payer}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_default_payer: !!checked })
                  }
                />
                <Label htmlFor="edit_is_default_payer" className="text-sm cursor-pointer">
                  Paga contas
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit_is_default_agent"
                  checked={formData.is_default_agent}
                  onCheckedChange={(checked) => {
                    setFormData({
                      ...formData,
                      is_default_agent: !!checked,
                    });
                  }}
                />
                <Label htmlFor="edit_is_default_agent" className="text-sm cursor-pointer">
                  Agente Padrão
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={!formData.name || isUpdating}>
              {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Agente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o agente "{selectedAgent?.name}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AgentsPage;
